import { useRef } from 'react';

import { BadgeCircle } from '@backrs_ui/common-ui';

import { PromptItem, DocumentType, ProfileItemType } from '@backrs_ui/grow-ui/src/models/models';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { getActiveLubavSession } from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';

import { Box, SxProps, Theme } from '@mui/material';

type SuggestedPromptsProps = Readonly<{
  prompts: PromptItem[];
  onSelect: (params: {
    message: string;
    LLMInput: string;
    requiresDocument?: DocumentType[];
    allowSkipDocument?: boolean;
    includeProfileItems?: ProfileItemType[];
  }) => void;
  disabled?: boolean;
}>;

export const SuggestedPrompts = ({
  prompts = [],
  onSelect,
  disabled = false,
}: SuggestedPromptsProps) => {
  const activeSession = useAppSelector(getActiveLubavSession);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  if (!activeSession || prompts.length === 0) {
    return null;
  }

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const container = scrollContainerRef.current;

    if (container) {
      // convert vertical wheel events to horizontal scrolling
      container.scrollLeft += e.deltaY;
      e.preventDefault();
    }
  };

  return (
    <Box sx={promptsContainerStyle}>
      <Box sx={promptLayoutStyle}>
        <Box ref={scrollContainerRef} sx={promptButtonsGrid} onWheel={handleWheel}>
          {prompts.map((prompt, index) => (
            <Box
              key={index}
              id={`suggested-prompt-${index}`}
              sx={promptButtonStyle}
              onClick={async () => {
                if (disabled) return;

                onSelect({
                  message: prompt.display,
                  LLMInput: prompt.input || '',
                  requiresDocument: prompt.requireDocument,
                  allowSkipDocument: prompt.allowSkipDocument,
                  includeProfileItems: prompt.includeProfileItems,
                });
              }}
            >
              {prompt.tag && (
                <Box sx={tagStyle}>
                  <BadgeCircle size="md" />
                  {prompt.tag}
                </Box>
              )}
              <span>{prompt.display}</span>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const tagStyle: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-md-regular'],
  fontWeight: 500,
  padding: `${backrs.spacing['spacing-none']} ${backrs.spacing['spacing-md']}`,
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
  mr: backrs.spacing['spacing-md'],
  mt: backrs.spacing['spacing-xxs'],
  mb: backrs.spacing['spacing-xxs'],
});

const promptsContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  paddingRight: backrs.spacing['spacing-none'],
});

const promptButtonStyle: SxProps<Theme> = ({ backrs, typography }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: 'fit-content',
  minWidth: 'fit-content',
  flexShrink: 0,
  py: backrs.spacing['spacing-xxs'],
  pl: backrs.spacing['spacing-xs'],
  pr: backrs.spacing['spacing-md'],
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: backrs.radius['radius-xl'],
  cursor: 'pointer',
  ...typography['text-md-regular'],
  fontWeight: 500,
});

const promptButtonsGrid: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  overflowX: 'auto',
  columnGap: backrs.spacing['spacing-lg'],
  paddingBottom: backrs.spacing['spacing-xl'],
  '&::-webkit-scrollbar': {
    height: backrs.spacing['spacing-xs'],
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: backrs.colors['Gray/100'],
    borderRadius: backrs.radius['radius-md'],
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: backrs.colors['Gray/400'],
    borderRadius: backrs.radius['radius-md'],
    '&:hover': {
      backgroundColor: backrs.colors['Gray/500'],
    },
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${backrs.colors['Gray/400']} ${backrs.colors['Gray/100']}`,
});

const promptLayoutStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};
